import { Search } from 'lucide-react'
import { useId, useState } from 'react'

import { NotImplemented } from '@/components/not-implemented'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { ApprovalTrail } from './approval-trail'

/**
 * "What happened to request X."
 *
 * The question a maker asks after submitting something and hearing nothing, and the question a
 * reviewer asks about a request that has already left the queue. Both are answerable — but only
 * one request at a time, and only by its id.
 *
 * That constraint is the API's and it is deliberate there: the audit trail is addressed by entity
 * and is not searchable, because an endpoint answering "every entry, any filter" would be a bulk
 * extract of the institution's activity behind a single permission. The consequence for this
 * screen is that a decided-requests list cannot be built, and the block below says so instead of
 * showing an empty table that reads as "nothing has ever been approved here".
 */

/** Request ids are UUIDv7, and the server lower-cases them before matching. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function RequestLookup() {
  const fieldId = useId()
  const [text, setText] = useState('')
  const [lookingUp, setLookingUp] = useState<string | null>(null)

  const trimmed = text.trim()
  const wellFormed = UUID.test(trimmed)

  const submit = () => {
    if (wellFormed) {
      setLookingUp(trimmed.toLowerCase())
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-card border border-border-subtle bg-surface p-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1 space-y-1.5">
            <Label htmlFor={fieldId}>Request id</Label>
            <Input
              id={fieldId}
              value={text}
              autoComplete="off"
              spellCheck={false}
              placeholder="0195f0c8-3b7e-7a41-9d2c-6f0e5a1b2c3d"
              className="font-mono"
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  submit()
                }
              }}
            />
          </div>
          <Button variant="primary" onClick={submit} disabled={!wellFormed}>
            <Search />
            Read the trail
          </Button>
        </div>

        <p className="mt-1.5 text-2xs text-muted">
          The id a dual-controlled operation reported back when it was queued, or the one quoted on
          a row in the queue. Nothing else finds a request: there is no search over descriptions,
          makers, or dates.
        </p>

        {trimmed !== '' && !wellFormed ? (
          <p className="mt-1.5 text-xs text-danger-text">
            That is not a request id. They are UUIDs, in the form shown above.
          </p>
        ) : null}
      </div>

      {lookingUp === null ? (
        <Alert tone="info">
          <Search aria-hidden />
          <div className="space-y-1">
            <AlertTitle>Enter a request id to see its history</AlertTitle>
            <AlertDescription>
              The trail records the submission, every refused countersignature attempt, and the
              decision — approved, refused, or lapsed — with both names against it. Reading it is
              itself an audited action, so nothing is fetched until you ask.
            </AlertDescription>
          </div>
        </Alert>
      ) : (
        // Keyed on the id. A second lookup is a different record, and the page index from the
        // first must not carry onto it — page 2 of a request whose trail is one page long returns
        // no rows, and this panel would render that as "nothing is recorded against this request
        // id", which is the one wrong answer an audit surface must never give.
        <ApprovalTrail key={lookingUp} requestId={lookingUp} enabled />
      )}

      <NotImplemented
        summary="A list of every request and how it ended — approved, refused, or lapsed — across the institution, filtered by module, branch, maker, checker and date."
        milestone="a Phase 1 API change, not a screen change"
        capabilities={[
          'No endpoint lists every approval request across the institution: GET /access/approvals/inbox returns only what is pending, unexpired, and yours to decide, and GET /access/approvals/mine returns only what you yourself submitted',
          'The audit trail is addressed by one entity at a time and is not searchable, by design',
          'So an institution-wide decided-request list, a rejection report, and an expiry report cannot be built from this API',
          'What exists instead is the per-request lookup above, for an id somebody already holds, and the Submitted by me tab for your own history',
        ]}
      />
    </div>
  )
}
